/**
 * Financial Summary Component
 *
 * Displays financial metrics: total revenue, avg transaction value, total transactions
 */

import { useTranslation } from "react-i18next";
import type { FinancialSummary as FinancialSummaryType } from "../types/overview-types";
import { DailyRevenueChart } from "./daily-revenue-chart";
import { MetricCard } from "./metric-card";
import { MetricCardWrapper } from "./metric-card-wrapper";
import { RevenueCustomerSplit } from "./revenue-customer-split";
import { TopServicesTable } from "./top-services-table";

interface FinancialSummaryProps {
  data: FinancialSummaryType;
}

export function FinancialSummary({ data }: FinancialSummaryProps) {
  const { t } = useTranslation("reports");

  return (
    <div className="space-y-4">
      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Basic: Total Revenue */}
        <MetricCard
          metric={{ ...data.totalRevenue, label: t("metrics.totalRevenue.label") }}
          tooltip={t("metrics.totalRevenue.tooltip")}
        />

        {/* Premium: Average Transaction Value */}
        <MetricCardWrapper
          metricKey="financial.averageTransactionValue"
          metric={
            data.averageTransactionValue
              ? { ...data.averageTransactionValue, label: t("metrics.avgTransaction.label") }
              : undefined
          }
          label={t("metrics.avgTransaction.label")}
          tooltip={t("metrics.avgTransaction.tooltip")}
        />

        {/* Premium: Total Transactions */}
        <MetricCardWrapper
          metricKey="financial.totalTransactions"
          metric={
            data.totalTransactions
              ? { ...data.totalTransactions, label: t("metrics.totalTransactions.label") }
              : undefined
          }
          label={t("metrics.totalTransactions.label")}
          tooltip={t("metrics.totalTransactions.tooltip")}
        />

        {/* Premium: Revenue per Customer */}
        <MetricCardWrapper
          metricKey="financial.revenuePerCustomer"
          metric={
            data.revenuePerCustomer
              ? { ...data.revenuePerCustomer, label: t("metrics.revenuePerCustomer.label") }
              : undefined
          }
          label={t("metrics.revenuePerCustomer.label")}
          tooltip={t("metrics.revenuePerCustomer.tooltip")}
        />

        {/* Premium: Average Customer Lifetime Value */}
        <MetricCardWrapper
          metricKey="financial.averageCustomerLifetimeValue"
          metric={
            data.averageCustomerLifetimeValue
              ? {
                  ...data.averageCustomerLifetimeValue,
                  label: t("metrics.avgCustomerLifetimeValue.label"),
                }
              : undefined
          }
          label={t("metrics.avgCustomerLifetimeValue.label")}
          tooltip={t("metrics.avgCustomerLifetimeValue.tooltip")}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
        <div className="lg:col-span-2">
          <DailyRevenueChart data={data.dailyRevenueTrend} />
        </div>
        <RevenueCustomerSplit
          current={data.revenueByCustomerType?.current ?? null}
          previous={data.revenueByCustomerType?.previous ?? null}
        />
      </div>

      {/* Top Performing Services */}
      <TopServicesTable data={data.topServices} />
    </div>
  );
}
